import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Camera, MapPin, ImageOff, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { api, fmtDateTime } from "../lib/api";
import { StaffLabel } from "./StaffAvatar";

// Authenticated inline image: streams the protected photo with the auth token,
// renders via a temporary object URL.
export function AuthedImg({ code, fileId, alt, className, onClick }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let alive = true;
    api.get(`/t/${code}/third-party/photos/${fileId}`, { responseType: "blob" })
      .then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        if (alive) setUrl(objectUrl);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [code, fileId]);

  if (failed) {
    return (
      <div className={`flex items-center justify-center bg-muted text-muted-foreground ${className}`}>
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  if (!url) return <div className={`bg-muted animate-pulse ${className}`} />;
  return <img src={url} alt={alt} className={className} onClick={onClick} loading="lazy" />;
}

// GPS photo gallery for a property's photos.
// Click a thumbnail -> dialog with the full photo + a map pin of where it was taken.
// Optional: canDelete(photo) + onDelete(photo) enable per-photo removal.
export function GpsPhotoGallery({ code, photos, title = "GPS-tagged photos", canDelete, onDelete }) {
  const [active, setActive] = useState(null);
  if (!photos || photos.length === 0) return null;

  return (
    <div data-testid="gps-photo-gallery">
      <p className="font-medium text-sm mb-2 flex items-center gap-1.5">
        <Camera className="h-4 w-4" /> {title}
        <Badge variant="secondary" className="text-[10px]">{photos.length}</Badge>
      </p>
      <div className="flex flex-wrap gap-2">
        {photos.map((p) => (
          <div key={p.file_id} className="relative">
            <button type="button" onClick={() => setActive(p)}
              className="group relative rounded-md overflow-hidden border focus-visible:ring-2 focus-visible:ring-ring"
              title="View photo & location" data-testid={`gps-photo-thumb-${p.file_id}`}>
              <AuthedImg code={code} fileId={p.file_id} alt={p.filename || "photo"}
                className="w-24 h-20 object-cover cursor-pointer transition-transform duration-200 group-hover:scale-105" />
              <span className={`absolute bottom-0 inset-x-0 flex items-center justify-center gap-0.5 text-[9px] py-0.5
                ${p.lat != null ? "bg-emerald-700/85 text-white" : "bg-amber-600/85 text-white"}`}>
                <MapPin className="h-2.5 w-2.5" /> {p.lat != null ? "GPS" : "No GPS"}
              </span>
            </button>
            {canDelete && onDelete && canDelete(p) && (
              <button type="button" onClick={() => onDelete(p)}
                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow focus-visible:ring-2 focus-visible:ring-ring"
                title="Remove this photo" data-testid={`gps-photo-delete-${p.file_id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      <GpsPhotoDialog code={code} photo={active} onClose={() => setActive(null)} />
    </div>
  );
}

// Standalone photo dialog (photo + capture meta + map pin). `extra` renders additional
// context (e.g., property info on report pages) above the metadata row.
export function GpsPhotoDialog({ code, photo, onClose, extra = null }) {
  return (
    <Dialog open={!!photo} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="gps-photo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base">
            <Camera className="h-4 w-4" /> {photo?.filename || "Photo"}
          </DialogTitle>
        </DialogHeader>
        {photo && (
          <div className="space-y-3">
            <AuthedImg code={code} fileId={photo.file_id} alt={photo.filename || "photo"}
              className="w-full max-h-80 object-contain rounded-md border bg-muted" />
            {extra}
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {photo.captured_at && <span data-testid="gps-photo-captured">Captured {fmtDateTime(photo.captured_at)} IST</span>}
              {photo.uploaded_by_name && (
                <span data-testid="gps-photo-uploader">by <StaffLabel code={code} userId={photo.uploaded_by} name={photo.uploaded_by_name} />{photo.uploaded_by_role ? ` (${String(photo.uploaded_by_role).replace(/_/g, " ")})` : ""}</span>
              )}
              {photo.lat != null ? (
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="gps-photo-coords">
                  <MapPin className="h-3 w-3 mr-0.5" /> {photo.lat.toFixed(6)}, {photo.lng.toFixed(6)}
                  {photo.accuracy != null ? ` (±${Math.round(photo.accuracy)}m)` : ""}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                  <MapPin className="h-3 w-3 mr-0.5" /> No GPS data on this photo
                </Badge>
              )}
            </div>
            {photo.lat != null && (
              <Card className="shadow-sm overflow-hidden">
                <CardContent className="p-0">
                  <div className="h-64 w-full" data-testid="gps-photo-map">
                    <MapContainer key={photo.file_id} center={[photo.lat, photo.lng]} zoom={17}
                      style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                      <CircleMarker center={[photo.lat, photo.lng]} radius={10}
                        pathOptions={{ color: "#047857", fillColor: "#10b981", fillOpacity: 0.85, weight: 3 }}>
                        <Popup>
                          <p className="text-xs font-medium">Photo taken here</p>
                          <p className="text-[11px]">{photo.lat.toFixed(6)}, {photo.lng.toFixed(6)}</p>
                        </Popup>
                      </CircleMarker>
                    </MapContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
